import styled from '@emotion/styled'
import { keyframes } from '@emotion/react'
import CloseButton from './CloseButton'
import Backdrop from './Backdrop'
import useEscModalHandler from 'shop/hooks/useEscModalHandler'
import { StyledHTMLElement } from 'shop/theme/types'
import { CSSProperties, useEffect, useState } from 'react'

interface Props {
  children: React.ReactNode
  handleCloseModal?: () => void
  canClose?: boolean
  zIndex?: number
  inModal?: boolean
  modalHeight?: CSSProperties['height'] // could this be replaced by FullscreenModal?
}

const BottomModal = ({
  children,
  handleCloseModal,
  canClose = true,
  zIndex = 30,
  inModal = false,
  modalHeight
}: Props) => {
  const [isClosing, setIsClosing] = useState(false)
  useEscModalHandler({ onClose: handleCloseModal })

  /** Handles animation onClose / calls close modal func after animation */
  const onClose = () => {
    if (!handleCloseModal) return
    setIsClosing(true)
    // Wait for animation to complete before actually closing
    setTimeout(() => {
      handleCloseModal()
    }, 200)
  }

  useEffect(() => {
    // if already in a Modal do not lock/unlock body scroll.
    if (inModal) return
    // onMount lock scroll behind BottomModal
    document.body.style.overflow = 'hidden'
    return () => {
      if (inModal) return
      // onDismount unlock scroll that was behind BottomModal
      document.body.style.overflow = 'visible'
    }
  }, [])

  return (
    <ModalContainer data-testid='bottomModal' zIndex={zIndex}>
      <Dialog isClosing={isClosing} modalHeight={modalHeight}>
        {handleCloseModal && canClose && (
          <CloseButton variant='bottomModal' handleCloseModal={onClose} />
        )}
        {children}
      </Dialog>
      <Backdrop onClick={handleCloseModal ? onClose : () => {}} />
    </ModalContainer>
  )
}

const ModalContainer = styled.div<StyledHTMLElement & { zIndex: number }>(
  ({ theme, zIndex }) => ({
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: zIndex,
    display: 'flex',
    [theme.mediaQueries.viewport7]: {
      top: theme.components.navbar.height
    }
  })
)

const slideUp = keyframes`
  0% {
    opacity: 0;
    transform: translateY(360px);
  }

  100% {
    opacity: 1;
    transform: translateY(0);
  }
`
const slideDownOut = keyframes`
  0% {
    opacity: 1;
    transform: translateY(0);
  }

  100% {
    opacity: 0;
    transform: translateY(360px);
  }
`

const Dialog = styled.div<
  StyledHTMLElement & {
    isClosing: boolean
    modalHeight?: CSSProperties['height']
  }
>(({ theme, isClosing, modalHeight }) => ({
  overflowY: 'auto',
  backgroundColor: 'white',
  height: modalHeight,
  maxWidth: '576px',
  width: '100%',
  position: 'absolute',
  display: 'flex',
  flexDirection: 'column',
  bottom: 0,
  left: 0,
  right: 0,
  margin: 'auto',
  zIndex: theme.zIndex.genericModal,
  padding: '0',
  animation: isClosing
    ? `${slideDownOut} 200ms ease-in forwards`
    : `${slideUp} 200ms ease-out`,
  borderTopRightRadius: modalHeight !== '100%' ? theme.borderRadius : '0px',
  borderTopLeftRadius: modalHeight !== '100%' ? theme.borderRadius : '0px',
  '> h1': {
    marginBottom: '24px'
  }
}))

export default BottomModal
